import { useDebounce } from '@tempo-io/fim3-components';
import { LifecycleFilter } from '@tempo-io/fim3-dto';
import { Input } from '@tempo-io/sui-input';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BaseDialog } from '@/common/components/base-dialog/BaseDialog';
import { useInfiniteScrollHandler } from '@/common/hooks/useInfiniteScrollHandler';
import { useFim3Api } from '@/features/intake/hooks/useFim3Api';

interface CustomerSelectionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedCustomerId?: number;
  excludedCustomerIds?: number[];
  onSave: (customerId: number) => void;
  title: string;
}

export const CustomerSelectionDialog = ({
  open,
  onOpenChange,
  selectedCustomerId,
  excludedCustomerIds = [],
  onSave,
  title,
}: CustomerSelectionDialogProps) => {
  const { t } = useTranslation();
  const { customersApi } = useFim3Api();
  const [searchInputValue, setSearchInputValue] = useState('');

  const debouncedSearch = useDebounce(searchInputValue, 250);
  const trimmedSearch = debouncedSearch.trim();

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } = customersApi.useGetAll(
    {
      lifecycleFilter: LifecycleFilter.Active,
      sort: { column: 'name', order: 'asc' },
      ...(trimmedSearch ? { search: trimmedSearch } : {}),
    },
    { enabled: open },
  );

  const customers = useMemo(() => data?.pages.flatMap((p) => p.data) ?? [], [data]);
  const excludedSet = useMemo(() => new Set(excludedCustomerIds), [excludedCustomerIds]);
  const visibleCustomers = useMemo(
    () => customers.filter((c) => !excludedSet.has(c.id) || c.id === selectedCustomerId),
    [customers, excludedSet, selectedCustomerId],
  );

  const handleScroll = useInfiniteScrollHandler({
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  });

  const handleCustomerSelect = (customerId: number) => {
    onSave(customerId);
    setSearchInputValue('');
    onOpenChange(false);
  };

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      setSearchInputValue('');
    }
    onOpenChange(isOpen);
  };

  return (
    <BaseDialog
      open={open}
      onOpenChange={handleOpenChange}
      title={title}
      description={t(($) => $.adaptivePlanner.itemPanel.customerField.selectCustomerFromList)}
      className="z-1400! h-4/6 overflow-hidden flex flex-col"
    >
      <div className="flex flex-col gap-4 min-h-0 flex-1">
        <Input
          type="text"
          value={searchInputValue}
          onChange={(e) => setSearchInputValue(e.target.value)}
          placeholder={t(($) => $.adaptivePlanner.itemPanel.customerField.searchCustomers)}
          data-testid="customer-selection-dialog-search"
        />

        <div
          className="flex flex-col gap-2 overflow-auto flex-1 min-h-0"
          onScroll={handleScroll}
          data-testid="customer-selection-dialog-list"
        >
          {visibleCustomers.map((customer) => (
            <button
              key={customer.id}
              type="button"
              data-testid={`customer-selection-dialog-row-${customer.id}`}
              onClick={() => handleCustomerSelect(customer.id)}
              className={`flex items-center justify-between rounded-md border px-3 py-2 text-sm text-left transition-colors hover:bg-bg-neutral-subtle cursor-pointer ${
                selectedCustomerId === customer.id
                  ? 'border-border-neutral bg-bg-neutral-subtle'
                  : 'border-border-neutral bg-bg-surface-base'
              }`}
            >
              <span className="truncate">{customer.name}</span>
            </button>
          ))}
          {isFetchingNextPage && (
            <div
              className="text-xs text-text-neutral-secondary text-center py-2"
              data-testid="customer-selection-dialog-loading-more"
            >
              {t(($) => $.adaptivePlanner.itemPanel.customerField.loadingMore)}
            </div>
          )}
        </div>
      </div>
    </BaseDialog>
  );
};
